/**
 * Web Push registration — the client half of "she texts you first". A proactive
 * run persists her message and the backend fans a push out to every
 * `push_subscriptions` row for the account; with no row, the message is only
 * visible the next time the user happens to open the app. Nothing in this client
 * ever created a row, so the single most valuable retention mechanic in the
 * product could not reach a closed tab.
 *
 * Consent-correct by construction: Web Push is a SEPARATE consent from her chat
 * cadence, so this never prompts on its own initiative — it registers only when
 * permission has ALREADY been granted, and otherwise returns a reason. The
 * explicit ask belongs to a user-initiated control, which passes `askPermission`.
 *
 * Every dependency (the SW registration, the permission read, the api call, the
 * server key) is injected, so the whole flow is unit-tested with no browser.
 */

/** Why a registration attempt did not produce a stored subscription. */
export type PushSkipReason =
  | 'unsupported'
  | 'no_server_key'
  | 'permission_denied'
  | 'subscribe_failed'
  | 'save_failed';

/** The outcome of {@link registerPushSubscription}. */
export type PushResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly reason: PushSkipReason };

/** The `PushManager` surface this needs — the browser's satisfies it. */
export interface PushManagerLike {
  getSubscription(): Promise<{ toJSON(): PushSubscriptionJSON } | null>;
  subscribe(options: {
    userVisibleOnly: boolean;
    applicationServerKey: Uint8Array;
  }): Promise<{ toJSON(): PushSubscriptionJSON }>;
}

/** The injected environment (DIP) — nothing here touches a global directly. */
export interface PushDeps {
  /** Resolves the active service-worker registration's push manager, or `null`. */
  pushManager(): Promise<PushManagerLike | null>;
  /** The current `Notification.permission` value. */
  permission(): NotificationPermission;
  /** Prompt for permission. Omitted ⇒ this never prompts (the default). */
  askPermission?(): Promise<NotificationPermission>;
  /** The VAPID application server key (base64url). Empty ⇒ push is not configured. */
  serverKey: string;
  /** Persist the subscription against the account (`POST /push/subscribe`). */
  save(subscription: PushSubscriptionJSON): Promise<boolean>;
}

/**
 * Decode a base64url VAPID key into the `Uint8Array` `PushManager.subscribe`
 * requires. Returns `null` for anything that is not decodable, so a
 * misconfigured key degrades to "no push" instead of throwing on boot.
 */
export function decodeServerKey(base64Url: string): Uint8Array | null {
  const trimmed = base64Url.trim();
  if (!trimmed) {
    return null;
  }
  const padded = trimmed.padEnd(trimmed.length + ((4 - (trimmed.length % 4)) % 4), '=');
  const base64 = padded.replace(/-/g, '+').replace(/_/g, '/');
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
  } catch {
    return null;
  }
}

/**
 * Ensure this device has a stored Web Push subscription. Idempotent: an existing
 * browser subscription is re-sent rather than re-created, so the server row
 * survives a reinstall or a rotated endpoint. Never throws — every failure is a
 * typed reason, because a push problem must not break the app shell.
 */
export async function registerPushSubscription(deps: PushDeps): Promise<PushResult> {
  const key = decodeServerKey(deps.serverKey);
  if (!key) {
    return { ok: false, reason: 'no_server_key' };
  }

  const manager = await deps.pushManager();
  if (!manager) {
    return { ok: false, reason: 'unsupported' };
  }

  let permission = deps.permission();
  if (permission === 'default' && deps.askPermission) {
    permission = await deps.askPermission();
  }
  if (permission !== 'granted') {
    return { ok: false, reason: 'permission_denied' };
  }

  let subscription: { toJSON(): PushSubscriptionJSON } | null;
  try {
    subscription =
      (await manager.getSubscription()) ??
      (await manager.subscribe({ userVisibleOnly: true, applicationServerKey: key }));
  } catch {
    return { ok: false, reason: 'subscribe_failed' };
  }

  const saved = await deps.save(subscription.toJSON());
  return saved ? { ok: true } : { ok: false, reason: 'save_failed' };
}

/** The browser's push manager off the ready service-worker registration, or `null`. */
export async function browserPushManager(): Promise<PushManagerLike | null> {
  const container = globalThis.navigator?.serviceWorker;
  if (!container || !('PushManager' in globalThis)) {
    return null;
  }
  const registration = await container.ready;
  return (registration.pushManager as unknown as PushManagerLike) ?? null;
}

/** The current notification permission, or `'denied'` where the API is absent. */
export function browserPermission(): NotificationPermission {
  return globalThis.Notification?.permission ?? 'denied';
}
